import type { GeometryQuestion } from './geometryTestNY';

export const geometryTestPA: GeometryQuestion[] = [
  {
    id: 1, type: 'multiple-choice', points: 2,
    topic: 'Points, Lines & Planes', standard: 'PA.CC.G.CO.1',
    question: 'How many points are needed to determine a unique line?',
    options: ['1', '2', '3', '4'],
    correctAnswer: 1,
    solution: [
      'Through any two distinct points, there is exactly one line.',
      'This is a fundamental postulate of Euclidean geometry.',
      'Answer: 2'
    ]
  },
  {
    id: 2, type: 'multiple-choice', points: 2,
    topic: 'Angle Pairs', standard: 'PA.CC.G.CO.9',
    question: 'Two angles are complementary. One measures (2x + 10)°. The other measures (3x − 5)°. What is x?',
    options: ['x = 17', 'x = 15', 'x = 20', 'x = 10'],
    correctAnswer: 0,
    solution: [
      'Complementary angles add up to 90°.',
      '(2x + 10) + (3x − 5) = 90',
      '5x + 5 = 90',
      '5x = 85',
      'x = 17',
      'Answer: x = 17'
    ]
  },
  {
    id: 3, type: 'multiple-choice', points: 2,
    topic: 'Triangle Congruence', standard: 'PA.CC.G.CO.8',
    question: 'In triangles RST and UVW, RS = UV, ST = VW, and angle S = angle V. Which postulate proves congruence?',
    options: ['SSS', 'SAS', 'ASA', 'AAS'],
    correctAnswer: 1,
    solution: [
      'SAS: two sides and the included angle are congruent.',
      'RS = UV (side), angle S = angle V (included angle), ST = VW (side).',
      'Answer: SAS'
    ]
  },
  {
    id: 4, type: 'multiple-choice', points: 2,
    topic: 'Pythagorean Theorem', standard: 'PA.CC.G.SRT.8',
    question: 'Is a triangle with sides 9, 40, and 41 a right triangle?',
    options: ['Yes, because 9² + 40² = 41²', 'No, because 9 + 40 ≠ 41', 'Yes, because 9 + 40 > 41', 'No, because 9² + 40² ≠ 41²'],
    correctAnswer: 0,
    solution: [
      'Check: 9² + 40² = 81 + 1600 = 1681',
      '41² = 1681',
      '1681 = 1681 ✓',
      'Yes, it is a right triangle.',
      'Answer: Yes, because 9² + 40² = 41²'
    ]
  },
  {
    id: 5, type: 'multiple-choice', points: 2,
    topic: 'Trigonometry', standard: 'PA.CC.G.SRT.6',
    question: 'In right triangle ABC with right angle at B, if AB = 5 and AC = 13, what is sin(C)?',
    options: ['5/13', '12/13', '5/12', '13/5'],
    correctAnswer: 0,
    solution: [
      'First find BC: BC² = AC² − AB² = 169 − 25 = 144, BC = 12',
      'sin(C) = opposite/hypotenuse = AB/AC = 5/13',
      'Answer: 5/13'
    ]
  },
  {
    id: 6, type: 'multiple-choice', points: 2,
    topic: 'Similarity', standard: 'PA.CC.G.SRT.5',
    question: 'Triangle ABC has sides 6, 8, 10. Triangle DEF has sides 9, 12, 15. Are they similar?',
    options: ['No, the sides are different', 'Yes, by SSS~ with ratio 2:3', 'Yes, by AA~', 'Cannot be determined'],
    correctAnswer: 1,
    solution: [
      'Check ratios: 6/9 = 2/3, 8/12 = 2/3, 10/15 = 2/3',
      'All three ratios are equal (2:3), so the triangles are similar by SSS~.',
      'Answer: Yes, by SSS~ with ratio 2:3'
    ]
  },
  {
    id: 7, type: 'multiple-choice', points: 2,
    topic: 'Coordinate Geometry', standard: 'PA.CC.G.GPE.5',
    question: 'What is the slope of a line perpendicular to y = (3/4)x − 2?',
    options: ['3/4', '−4/3', '4/3', '−3/4'],
    correctAnswer: 1,
    solution: [
      'The slope of y = (3/4)x − 2 is 3/4.',
      'Perpendicular slope = negative reciprocal = −4/3',
      'Answer: −4/3'
    ]
  },
  {
    id: 8, type: 'multiple-choice', points: 2,
    topic: 'Circle Theorems', standard: 'PA.CC.G.C.2',
    question: 'A chord is 10 cm long. The radius of the circle is 13 cm. How far is the chord from the center?',
    options: ['3 cm', '12 cm', '√69 cm', '5 cm'],
    correctAnswer: 1,
    solution: [
      'The perpendicular from the center bisects the chord.',
      'Half chord = 5 cm; radius = 13 cm.',
      'distance² = 13² − 5² = 169 − 25 = 144',
      'distance = 12 cm',
      'Answer: 12 cm'
    ]
  },
  {
    id: 9, type: 'multiple-choice', points: 2,
    topic: 'Volume', standard: 'PA.CC.G.GMD.3',
    question: 'A cone and a cylinder have the same base and height. What is the ratio of the cone\'s volume to the cylinder\'s volume?',
    options: ['1:2', '1:3', '2:3', '1:4'],
    correctAnswer: 1,
    solution: [
      'Volume of cylinder = πr²h',
      'Volume of cone = (1/3)πr²h',
      'Ratio = (1/3)πr²h : πr²h = 1:3',
      'Answer: 1:3'
    ]
  },
  {
    id: 10, type: 'multiple-choice', points: 2,
    topic: 'Transformations', standard: 'PA.CC.G.CO.5',
    question: 'A point (x, y) is rotated 270° counterclockwise about the origin. What are the new coordinates?',
    options: ['(−y, x)', '(y, −x)', '(−x, −y)', '(x, −y)'],
    correctAnswer: 1,
    solution: [
      '270° counterclockwise = 90° clockwise.',
      'Rule for 90° clockwise: (x, y) → (y, −x)',
      'Answer: (y, −x)'
    ]
  },
  {
    id: 11, type: 'multiple-choice', points: 2,
    topic: 'Parallelogram', standard: 'PA.CC.G.CO.11',
    question: 'In parallelogram ABCD, AB = 3x − 1 and CD = 2x + 5. What is AB?',
    options: ['17', '11', '6', '23'],
    correctAnswer: 0,
    solution: [
      'Opposite sides of a parallelogram are equal.',
      '3x − 1 = 2x + 5',
      'x = 6',
      'AB = 3(6) − 1 = 17',
      'Answer: AB = 17'
    ]
  },
  {
    id: 12, type: 'multiple-choice', points: 2,
    topic: 'Dilation', standard: 'PA.CC.G.SRT.1',
    question: 'A triangle with area 20 is dilated by a scale factor of 3. What is the area of the new triangle?',
    options: ['60', '180', '9', '40'],
    correctAnswer: 1,
    solution: [
      'When a figure is dilated by scale factor k, its area is multiplied by k².',
      'New area = 20 × 3² = 20 × 9 = 180',
      'Answer: 180'
    ]
  },
  {
    id: 13, type: 'multiple-choice', points: 2,
    topic: 'Angle in Circle', standard: 'PA.CC.G.C.2',
    question: 'Two chords intersect inside a circle. The intercepted arcs are 80° and 120°. What is the angle formed?',
    options: ['100°', '200°', '40°', '80°'],
    correctAnswer: 0,
    solution: [
      'When two chords intersect inside a circle, the angle = (1/2)(sum of intercepted arcs)',
      'Angle = (1/2)(80° + 120°) = (1/2)(200°) = 100°',
      'Answer: 100°'
    ]
  },
  {
    id: 14, type: 'multiple-choice', points: 2,
    topic: 'Area of Regular Polygon', standard: 'PA.CC.G.GMD.1',
    question: 'A regular hexagon with side 4 has an apothem of 2√3. What is its area?',
    options: ['24√3', '48√3', '12√3', '16√3'],
    correctAnswer: 0,
    solution: [
      'Area of regular polygon = (1/2) × perimeter × apothem',
      'Perimeter = 6 × 4 = 24',
      'Area = (1/2)(24)(2√3) = 24√3',
      'Answer: 24√3'
    ]
  },
  {
    id: 15, type: 'multiple-choice', points: 2,
    topic: 'Equation of Circle', standard: 'PA.CC.G.GPE.1',
    question: 'A circle passes through (0, 0) and has center (3, 4). What is its radius?',
    options: ['5', '7', '√7', '25'],
    correctAnswer: 0,
    solution: [
      'Radius = distance from center to point on circle',
      'r = √[(3−0)² + (4−0)²] = √[9 + 16] = √25 = 5',
      'Answer: 5'
    ]
  },
  {
    id: 16, type: 'multiple-choice', points: 2,
    topic: 'Special Right Triangles', standard: 'PA.CC.G.SRT.8',
    question: 'In a 45-45-90 triangle, the hypotenuse is 10√2. What is the length of each leg?',
    options: ['10', '5√2', '20', '5'],
    correctAnswer: 0,
    solution: [
      'In a 45-45-90 triangle: hypotenuse = leg × √2',
      'leg = hypotenuse / √2 = 10√2 / √2 = 10',
      'Answer: 10'
    ]
  },
  {
    id: 17, type: 'multiple-choice', points: 2,
    topic: 'Proofs', standard: 'PA.CC.G.CO.9',
    question: 'In a proof, the statement "Vertical angles are congruent" is used as a:',
    options: ['Given', 'Theorem', 'Postulate', 'Definition'],
    correctAnswer: 1,
    solution: [
      'The Vertical Angles Theorem is a proven statement in geometry.',
      'Theorems are statements that have been proven using postulates, definitions, and previously proven theorems.',
      'Answer: Theorem'
    ]
  },
  {
    id: 18, type: 'multiple-choice', points: 2,
    topic: 'Coordinate Geometry', standard: 'PA.CC.G.GPE.7',
    question: 'What is the perimeter of a triangle with vertices A(0,0), B(6,0), and C(0,8)?',
    options: ['24', '14', '24 + 10', '24'],
    correctAnswer: 0,
    solution: [
      'AB = 6 (horizontal), AC = 8 (vertical)',
      'BC = √(6² + 8²) = √(36 + 64) = √100 = 10',
      'Perimeter = 6 + 8 + 10 = 24',
      'Answer: 24'
    ]
  },
  {
    id: 19, type: 'multiple-choice', points: 2,
    topic: 'Exterior Angle', standard: 'PA.CC.G.CO.10',
    question: 'An exterior angle of a triangle is 115°. One non-adjacent interior angle is 60°. What is the other non-adjacent interior angle?',
    options: ['55°', '65°', '45°', '25°'],
    correctAnswer: 0,
    solution: [
      'Exterior Angle Theorem: exterior angle = sum of two non-adjacent interior angles.',
      '115° = 60° + x',
      'x = 55°',
      'Answer: 55°'
    ]
  },
  {
    id: 20, type: 'multiple-choice', points: 2,
    topic: 'Surface Area of Sphere', standard: 'PA.CC.G.GMD.3',
    question: 'What is the surface area of a sphere with radius 7?',
    options: ['196π', '49π', '98π', '343π'],
    correctAnswer: 0,
    solution: [
      'Surface area of a sphere = 4πr²',
      'SA = 4π(7)² = 4π(49) = 196π',
      'Answer: 196π'
    ]
  },
  {
    id: 21, type: 'multiple-choice', points: 2,
    topic: 'Inscribed Quadrilateral', standard: 'PA.CC.G.C.3',
    question: 'A quadrilateral is inscribed in a circle. Opposite angles A and C satisfy:',
    options: ['angle A = angle C', 'angle A + angle C = 90°', 'angle A + angle C = 180°', 'angle A − angle C = 90°'],
    correctAnswer: 2,
    solution: [
      'Opposite angles of a cyclic quadrilateral (inscribed in a circle) are supplementary.',
      'angle A + angle C = 180°',
      'Answer: angle A + angle C = 180°'
    ]
  },
  {
    id: 22, type: 'multiple-choice', points: 2,
    topic: 'Geometric Probability', standard: 'PA.CC.G.MG.3',
    question: 'A point is chosen at random inside a 10 × 10 square. A circle of radius 3 is inside the square. What is the probability the point is inside the circle?',
    options: ['9π/100', '3π/10', '9/100', 'π/10'],
    correctAnswer: 0,
    solution: [
      'Area of circle = π(3)² = 9π',
      'Area of square = 10 × 10 = 100',
      'Probability = 9π / 100',
      'Answer: 9π/100'
    ]
  },
  {
    id: 23, type: 'multiple-choice', points: 2,
    topic: 'Translations', standard: 'PA.CC.G.CO.4',
    question: 'Triangle ABC is translated so that A(1, 2) maps to A\'(4, −1). What is the translation vector?',
    options: ['(3, −3)', '(−3, 3)', '(5, 1)', '(3, 3)'],
    correctAnswer: 0,
    solution: [
      'Translation vector = (x\' − x, y\' − y)',
      'Vector = (4 − 1, −1 − 2) = (3, −3)',
      'Answer: (3, −3)'
    ]
  },
  {
    id: 24, type: 'multiple-choice', points: 2,
    topic: 'Volume of Frustum', standard: 'PA.CC.G.GMD.3',
    question: 'A frustum (truncated cone) has radii 3 and 6 and height 4. Its volume is (1/3)πh(r₁² + r₁r₂ + r₂²). What is the volume?',
    options: ['84π', '28π', '252π', '63π'],
    correctAnswer: 0,
    solution: [
      'V = (1/3)π(4)(3² + 3×6 + 6²)',
      'V = (1/3)π(4)(9 + 18 + 36)',
      'V = (1/3)π(4)(63)',
      'V = (1/3)(252π) = 84π',
      'Answer: 84π'
    ]
  },
  {
    id: 25, type: 'multiple-choice', points: 2,
    topic: 'Angle Bisector', standard: 'PA.CC.G.CO.9',
    question: 'The incenter of a triangle is the point where:',
    options: [
      'The three medians meet',
      'The three altitudes meet',
      'The three angle bisectors meet',
      'The three perpendicular bisectors meet'
    ],
    correctAnswer: 2,
    solution: [
      'The incenter is the intersection of the three angle bisectors.',
      'It is equidistant from all three sides of the triangle.',
      'Answer: The three angle bisectors meet'
    ]
  },
  {
    id: 26, type: 'multiple-choice', points: 2,
    topic: 'Circumcenter', standard: 'PA.CC.G.C.3',
    question: 'The circumcenter of a triangle is equidistant from:',
    options: ['The three sides', 'The three vertices', 'The midpoints of the sides', 'The three altitudes'],
    correctAnswer: 1,
    solution: [
      'The circumcenter is the intersection of the perpendicular bisectors.',
      'It is equidistant from all three vertices (it is the center of the circumscribed circle).',
      'Answer: The three vertices'
    ]
  },
  {
    id: 27, type: 'multiple-choice', points: 2,
    topic: 'Parallel Lines', standard: 'PA.CC.G.CO.9',
    question: 'Lines l and m are parallel. A transversal creates alternate exterior angles of (7x − 5)° and (5x + 15)°. What is x?',
    options: ['x = 10', 'x = 5', 'x = 15', 'x = 20'],
    correctAnswer: 0,
    solution: [
      'Alternate exterior angles are congruent when lines are parallel.',
      '7x − 5 = 5x + 15',
      '2x = 20',
      'x = 10',
      'Answer: x = 10'
    ]
  },
  {
    id: 28, type: 'multiple-choice', points: 2,
    topic: 'Medians', standard: 'PA.CC.G.CO.10',
    question: 'The centroid divides each median in the ratio:',
    options: ['1:1', '1:2', '2:1', '1:3'],
    correctAnswer: 2,
    solution: [
      'The centroid divides each median in the ratio 2:1 from vertex to midpoint.',
      'The centroid is 2/3 of the way from each vertex to the opposite midpoint.',
      'Answer: 2:1'
    ]
  },
  {
    id: 29, type: 'multiple-choice', points: 2,
    topic: 'Arc Length', standard: 'PA.CC.G.C.5',
    question: 'A circle has diameter 20. What is the arc length of a 72° central angle?',
    options: ['4π', '2π', '8π', '10π'],
    correctAnswer: 0,
    solution: [
      'Radius = 10',
      'Arc length = (θ/360°) × 2πr',
      'Arc length = (72/360) × 2π(10)',
      'Arc length = (1/5) × 20π = 4π',
      'Answer: 4π'
    ]
  },
  {
    id: 30, type: 'multiple-choice', points: 2,
    topic: 'Cross-Sections', standard: 'PA.CC.G.GMD.4',
    question: 'A vertical plane cuts through a sphere through its center. What is the cross-section?',
    options: ['Ellipse', 'Circle equal to the great circle', 'Semicircle', 'Rectangle'],
    correctAnswer: 1,
    solution: [
      'When a plane cuts through the center of a sphere, the cross-section is a great circle.',
      'A great circle has the same radius as the sphere.',
      'Answer: Circle equal to the great circle'
    ]
  }
];
